'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { ProductTypes } from 'modules/products'
import Button from 'shared/ui/Button'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import useCartStore from '../../../store/cartStore'
import { CartItem } from '../../../types'

import styles from './RemoveLastProductModal.module.scss'

interface RemoveLastProductModalProps {
  open: boolean
  onClose: () => void
  product: ProductTypes
  onQuantityChange: () => void
  cartItemId?: string
}

const RemoveLastProductModal = ({
  cartItemId,
  onQuantityChange,
  product,
  open,
  onClose
}: RemoveLastProductModalProps) => {
  const [loading, setLoading] = useState(false)
  const session = useSession()
  const { removeFromCart, getItemByProductId, hasPendingOperations } =
    useCartStore()

  useEffect(() => {
    if (open) {
      setLoading(false)
    }
  }, [open])

  const handleSubmit = () => {
    setLoading(true)

    const item: CartItem | null = getItemByProductId(product.id)
    const itemIdToRemove = cartItemId || item?.id

    if (itemIdToRemove) {
      removeFromCart({ itemId: itemIdToRemove }, session)
        .then(() => {
          onQuantityChange()
          onClose()
        })
        .catch(err => {
          toast.error(getApiErrors(err))
          reportSentryError(err, 'Failed to remove product from cart')
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      setLoading(false)
      onClose()
    }
  }

  return (
    <Modal
      id='remove-product-from-cart-modal'
      onClose={onClose}
      open={open}
      size='small'
    >
      <ModalTitle>Potwierdź usunięcie produktu</ModalTitle>
      {hasPendingOperations() ? (
        <div className={styles.loaderWrapper}>
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <ModalDescription>
            Czy jesteś pewien, że chcesz usunąć <b>{product.name}</b> ze swojego
            koszyka?
          </ModalDescription>
          <div className={styles.buttonsWrapper}>
            <Button
              fullWidth
              variant='primaryOutline'
              loading={loading}
              disabled={loading}
              onClick={handleSubmit}
            >
              Potwierdź
            </Button>
            <Button
              fullWidth
              disabled={loading}
              onClick={onClose}
              variant='neutralOutline'
            >
              Anuluj
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}

export default RemoveLastProductModal
